import axios from 'axios';
import { ArcElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { getApiV1Url } from './config/api';
import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Pie } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import './styles/App.css';
import Filter from './components/Filters/Filter';
import DetailInfo from './components/LabList/DetailInfo';
import Manufacturers from './components/LabList/Мanufacturers';
import Pagination from './components/Pagination';
import BarChartLabExam from './components/LabList/BarChartLabExam';
import { getPdfButtonLabel, useRegistryPdfExport } from './hooks/useRegistryPdfExport';
import { buildLabExamRegistryCard, buildLabExamsSummary, describeLabExamFilters } from './utils/labExamsPdfUtils';
import { ResponsivePie } from '@nivo/pie';
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, Title);

const EXAM_RESULT_COLORS = {
  resultColors: {
    'Соответствует НД': 'rgba(126, 178, 70, 1)',
    'Не соответствует НД (фальсификат)': 'rgba(237, 93, 98, 1)',
    'Не соответствует НД (брак)': 'rgba(255, 166, 0, 1)',
    'Нет данных': 'rgba(196, 196, 196, 1)',
  },
  base: [
    'rgba(196, 196, 196, 1)',
    'rgba(237, 93, 98, 1)',
    'rgba(126, 178, 70, 1)',
    'rgba(255, 166, 0, 1)',
  ],
  dimmed: [
    'rgba(126, 178, 70, 0.3)',
    'rgba(237, 93, 98, 0.3)',
    'rgba(255, 166, 0, 0.3)',
    'rgba(255, 166, 0, 0.3)',
  ]
};

export interface LabExam {
  id?: number;
  state_company: string;
  region_company: string;
  address_company: string;
  name_company: string;
  inn_company: string;
  exam_date: string;
  object_name: string;
  geomaps: string;
  object_adres: string;
  reason_request: string;
  exploitation_time: string;
  customer: string;
  product_name: string;
  technical_documentation: string;
  compliance_requirements: string;
  exam_result: string;
  welding_joint_type?: string;
  manufacture: number;
  status: boolean;
}

type SortField = keyof LabExam | null;
type SortDirection = 'asc' | 'desc' | null;

const getUniqueManufacturers = (data: LabExam[]) => {
  const seen = new Set();
  return data.filter(item => {
    const key = `${item.region_company}|${item.address_company}|${item.name_company}|${item.inn_company}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Чтобы не было тестов в диаграмме
const filterTestData = (data: LabExam[]) => {
  return data.filter(item => {
    const testKeywords = ['тест', 'test', 'проверка', 'check', 'demo', 'демо', 'пример', 'example'];

    const fieldsToCheck = [
      item.name_company,
      item.inn_company,
      item.product_name,
      item.object_name,
      item.object_adres,
      item.customer,
      item.reason_request
    ];

    const hasTestData = fieldsToCheck.some(field => {
      if (!field) return false;
      return testKeywords.some(keyword =>
        field.toLowerCase().includes(keyword.toLowerCase())
      );
    });

    return !hasTestData;
  });
};

interface LabExamPageProps {
  isFilterOpen?: boolean;
  setIsFilterOpen?: (open: boolean) => void;
  weldedJointsOnly?: boolean;
}

function LabExamPage({ isFilterOpen: propIsFilterOpen, setIsFilterOpen: propSetIsFilterOpen, weldedJointsOnly = false }: LabExamPageProps = {}) {
  const navigate = useNavigate();
  const registryName = weldedJointsOnly ? 'Испытания сварных соединений' : 'Лабораторные испытания';
  const contentRef = useRef<HTMLDivElement>(null);
  const [labList, setLabList] = useState<LabExam[]>([]);
  const [filteredLabList, setFilteredLabList] = useState<LabExam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filteredExamResults, setFilteredExamResults] = useState<string[]>([]);
  const [filteredYears, setFilteredYears] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [filterFilters, setFilterFilters] = useState<{
    manufacturerName: string[];
    manufacturerInn: string[];
    region: string[];
    objectAddress: string[];
    weldingJointType: string[];
  }>({
    manufacturerName: [],
    manufacturerInn: [],
    region: [],
    objectAddress: [],
    weldingJointType: [],
  });

  const memoInitialFilters = useMemo(() => ({
    manufacturerName: filterFilters.manufacturerName,
    manufacturerInn: filterFilters.manufacturerInn,
    region: filterFilters.region,
    objectAddress: filterFilters.objectAddress,
    weldingJointType: filterFilters.weldingJointType,
  }), [filterFilters]);

  const [manufacturersPage, setManufacturersPage] = useState(1);
  const [detailInfoPage, setDetailInfoPage] = useState(1);
  const [itemsPerPageManufacturers, setItemsPerPageManufacturers] = useState(10);
  const [itemsPerPageDetailInfo, setItemsPerPageDetailInfo] = useState(10);

  const [manufacturersSortField, setManufacturersSortField] = useState<SortField>(null);
  const [manufacturersSortDirection, setManufacturersSortDirection] = useState<SortDirection>(null);
  const [detailInfoSortField, setDetailInfoSortField] = useState<SortField>(null);
  const [detailInfoSortDirection, setDetailInfoSortDirection] = useState<SortDirection>(null);

  const [labExamDate, setLabExamDate] = useState<string | null>(null);
  const { pdfProgress, startPdfExport, pdfReport } = useRegistryPdfExport();

  const [viewportWidth, setViewportWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const isFilterOpen = propIsFilterOpen !== undefined ? propIsFilterOpen : false;
  const setIsFilterOpen = propSetIsFilterOpen !== undefined ? propSetIsFilterOpen : () => { };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const access = localStorage.getItem('access');

        const response = await axios.get(getApiV1Url('LAB_EXAMS_LIST'), {
          headers: access ? { Authorization: `Bearer ${access}` } : {},
          timeout: 30000
        });

        if (weldedJointsOnly) {
          setLabExamDate(null);
        } else {
          const dateResponse = await axios.get(getApiV1Url('DATE'), {
            headers: access ? { Authorization: `Bearer ${access}` } : {},
            timeout: 10000
          });

          if (dateResponse.data && dateResponse.data.date_updated) {
            const date = new Date(dateResponse.data.date_updated);
            const formattedDate = date.toLocaleDateString('ru-RU', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
            setLabExamDate(formattedDate);
          }
        }

        const rows = Array.isArray(response.data) ? response.data : [];
        const exams = rows.filter((item: LabExam) =>
          weldedJointsOnly ? !!item.welding_joint_type?.trim() : !item.welding_joint_type?.trim()
        );
        setLabList(exams);
        setFilteredLabList(exams);
      } catch (err: any) {
        if (err.code === 'ECONNABORTED') {
          setError('Превышено время ожидания ответа от сервера');
        } else {
          setError('Ошибка при загрузке данных');
        }
        console.error('Ошибка загрузки:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [weldedJointsOnly]);

  const applyAllFilters = useMemo(() => {
    return (data: LabExam[]) => {
      let filtered = [...data];

      if (filterFilters.manufacturerName.length > 0) {
        filtered = filtered.filter(item =>
          item.name_company && filterFilters.manufacturerName.some(name =>
            item.name_company.toLowerCase().includes(name.toLowerCase())
          )
        );
      }

      if (filterFilters.manufacturerInn.length > 0) {
        filtered = filtered.filter(item =>
          item.inn_company && filterFilters.manufacturerInn.some(inn =>
            item.inn_company.toLowerCase().includes(inn.toLowerCase())
          )
        );
      }

      if (filterFilters.region.length > 0) {
        filtered = filtered.filter(item =>
          item.region_company && filterFilters.region.some(region =>
            item.region_company.toLowerCase().includes(region.toLowerCase())
          )
        );
      }

      if (filterFilters.objectAddress.length > 0) {
        filtered = filtered.filter(item =>
          item.object_adres && filterFilters.objectAddress.some(address =>
            item.object_adres.toLowerCase().includes(address.toLowerCase())
          )
        );
      }

      if (filterFilters.weldingJointType.length > 0) {
        filtered = filtered.filter(item =>
          item.welding_joint_type && filterFilters.weldingJointType.includes(item.welding_joint_type)
        );
      }

      if (filteredExamResults.length > 0) {
        filtered = filtered.filter(item =>
          item.exam_result && filteredExamResults.includes(item.exam_result)
        );
      }

      if (filteredYears.length > 0) {
        filtered = filtered.filter(item =>
          item.exam_date && filteredYears.includes(item.exam_date.slice(0, 4))
        );
      }

      if (selectedRegions.length > 0) {
        filtered = filtered.filter(item =>
          item.region_company && selectedRegions.some(region =>
            region && item.region_company.toLowerCase() === region.toLowerCase()
          )
        );
      }

      return filtered;
    };
  }, [filterFilters, filteredExamResults, filteredYears, selectedRegions]);

  useEffect(() => {
    const filtered = applyAllFilters(labList);
    setFilteredLabList(filtered);
    setManufacturersPage(1);
    setDetailInfoPage(1);
  }, [labList, applyAllFilters]);

  const handleFilterChange = (newFilters: {
    manufacturerName: string[];
    manufacturerInn: string[];
    region: string[];
    objectAddress: string[];
    weldingJointType: string[];
  }) => {
    setFilterFilters(newFilters);
  };

  const handleExamResultChange = (results: string[]) => {
    setFilteredExamResults(results);
  };

  const handleResetFilters = () => {
    setSelectedRegions([]);
    setFilteredExamResults([]);
    setFilteredYears([]);
    setFilterFilters({
      manufacturerName: [],
      manufacturerInn: [],
      region: [],
      objectAddress: [],
      weldingJointType: [],
    });
    setFilteredLabList(labList);
    setManufacturersPage(1);
    setDetailInfoPage(1);
  };

  useEffect(() => {
    if (isFilterOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFilterOpen) {
        setIsFilterOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isFilterOpen, setIsFilterOpen]);

  const sortData = (data: LabExam[], field: SortField, direction: SortDirection) => {
    if (field === null || direction === null) {
      return data;
    }

    return [...data].sort((a, b) => {
      if (field === 'exam_date') {
        const aTime = a.exam_date ? new Date(String(a.exam_date)).getTime() : 0;
        const bTime = b.exam_date ? new Date(String(b.exam_date)).getTime() : 0;
        return direction === 'asc' ? aTime - bTime : bTime - aTime;
      }

      const aRaw = a[field];
      const bRaw = b[field];

      //  для корректного сравнения и чтобы не ругался TS
      const aStr =
        typeof aRaw === 'boolean'
          ? aRaw
            ? '1'
            : '0'
          : aRaw === undefined || aRaw === null
            ? ''
            : String(aRaw).toLowerCase();
      const bStr =
        typeof bRaw === 'boolean'
          ? bRaw
            ? '1'
            : '0'
          : bRaw === undefined || bRaw === null
            ? ''
            : String(bRaw).toLowerCase();

      const cmp = aStr.localeCompare(bStr, 'ru', { numeric: true, sensitivity: 'base' });
      return direction === 'asc' ? cmp : -cmp;
    });
  };

  const handleManufacturersSort = (field: keyof LabExam) => {
    if (field === manufacturersSortField) {
      if (manufacturersSortDirection === 'asc') {
        setManufacturersSortDirection('desc');
      } else if (manufacturersSortDirection === 'desc') {
        setManufacturersSortField(null);
        setManufacturersSortDirection(null);
      }
    } else {
      setManufacturersSortField(field);
      setManufacturersSortDirection('asc');
    }
  };

  const handleDetailInfoSort = (field: keyof LabExam) => {
    if (field === detailInfoSortField) {
      if (detailInfoSortDirection === 'asc') {
        setDetailInfoSortDirection('desc');
      } else if (detailInfoSortDirection === 'desc') {
        setDetailInfoSortField(null);
        setDetailInfoSortDirection(null);
      } else {
        setDetailInfoSortDirection('asc');
      }
    } else {
      setDetailInfoSortField(field);
      setDetailInfoSortDirection('asc');
    }
  };

  const handlePieChartClickNivo = (datum: any) => {
    const label = datum.id;
    const newResults = filteredExamResults.includes(label)
      ? filteredExamResults.filter((r: string) => r !== label)
      : [...filteredExamResults, label];

    setFilteredExamResults(newResults);
  };

  const handleBarClick = (year: string) => {
    const newYears = filteredYears.includes(year)
      ? filteredYears.filter((y: string) => y !== year)
      : [...filteredYears, year];

    setFilteredYears(newYears);
  };

  const handleRegionSelect = (region: string) => {
    if (!region || !labList) return;

    console.log('Выбран регион:', region);

    const exactMatch = labList.find((item: LabExam) =>
      item.region_company && region && item.region_company.toLowerCase() === region.toLowerCase()
    );

    const partialMatch = !exactMatch ? labList.find((item: LabExam) =>
      item.region_company && region && (
        item.region_company.toLowerCase().includes(region.toLowerCase()) ||
        region.toLowerCase().includes(item.region_company.toLowerCase())
      )
    ) : null;

    const matchedRegion = exactMatch ? exactMatch.region_company : (partialMatch ? partialMatch.region_company : null);

    if (matchedRegion) {
      const newSelectedRegions = selectedRegions.some((r: string) => r && matchedRegion && r.toLowerCase() === matchedRegion.toLowerCase())
        ? selectedRegions.filter((r: string) => r && matchedRegion && r.toLowerCase() !== matchedRegion.toLowerCase())
        : [...selectedRegions, matchedRegion];

      setSelectedRegions(newSelectedRegions);
    }
  };

  const handleOpenLabDetails = useCallback((lab: LabExam) => {
    if (!lab.id) {
      return;
    }
    navigate(`/lab-exam/${lab.id}`, { state: { lab } });
  }, [navigate]);

  const examResultsCount = new Map<string, number>();
  filteredLabList.forEach((lab: LabExam) => {
    let result = lab.exam_result || 'Нет данных';
    if (result.toLowerCase() === 'нет данных' || result === '') {
      result = 'Нет данных';
    }
    examResultsCount.set(result, (examResultsCount.get(result) || 0) + 1);
  });

  const getBackgroundColors = () => {
    const resultKeys = Array.from(examResultsCount.keys());
    return resultKeys.map((result, idx) => {
      const resultColors = EXAM_RESULT_COLORS.resultColors as Record<string, string>;
      if (resultColors[result]) {
        if (filteredExamResults.length > 0 && !filteredExamResults.includes(result)) {
          return resultColors[result].replace(/0\.8\)/, '0.3)');
        }
        return resultColors[result];
      } else {
        const baseColor = EXAM_RESULT_COLORS.base[idx % EXAM_RESULT_COLORS.base.length];
        if (filteredExamResults.length > 0 && !filteredExamResults.includes(result)) {
          return baseColor.replace(/0\.8\)/, '0.3)');
        }
        return baseColor;
      }
    });
  };

  const examResultsPieData = Array.from(examResultsCount.keys()).map((label, idx) => ({
    id: label,
    label: label === 'Не соответствует (фальсификат)' || label === 'Не соответствует НД (фальсификат)'
      ? 'Не соответствует\n(фальсификат)'
      : label,
    value: Array.from(examResultsCount.values())[idx],
    color: getBackgroundColors()[idx] || '#ccc',
  })).filter(item => item.value > 0);

  const getManufacturersPieData = () => {
    const manufacturersResults = new Map<string, number>();

    const dataToProcess = selectedRegions.length > 0
      ? filteredLabList.filter(lab =>
        lab.region_company && selectedRegions.some(region =>
          region && lab.region_company.toLowerCase() === region.toLowerCase()
        )
      )
      : filteredLabList;

    dataToProcess.forEach((lab: LabExam) => {
      let result = lab.exam_result || 'Нет данных';
      if (result.toLowerCase() === 'нет данных' || result === '') {
        result = 'Нет данных';
      }
      manufacturersResults.set(result, (manufacturersResults.get(result) || 0) + 1);
    });

    const resultKeys = Array.from(manufacturersResults.keys());
    const colors = resultKeys.map((result, idx) => {
      const resultColors = EXAM_RESULT_COLORS.resultColors as Record<string, string>;
      return resultColors[result] || EXAM_RESULT_COLORS.base[idx % EXAM_RESULT_COLORS.base.length];
    });

    return resultKeys.map((label, idx) => ({
      id: label,
      label: label === 'Не соответствует (фальсификат)' || label === 'Не соответствует НД (фальсификат)'
        ? 'Не соответствует\n(фальсификат)'
        : label,
      value: Array.from(manufacturersResults.values())[idx],
      color: colors[idx] || '#ccc',
    })).filter(item => item.value > 0);
  };

  const getObjectsPieData = () => {
    const objectsResults = new Map<string, number>();

    const dataToProcess = selectedRegions.length > 0
      ? filteredLabList.filter(lab =>
        lab.region_company && selectedRegions.some(region =>
          region && lab.region_company.toLowerCase() === region.toLowerCase()
        )
      )
      : filteredLabList;

    dataToProcess.forEach((lab: LabExam) => {
      let result = lab.exam_result || 'Нет данных';
      if (result.toLowerCase() === 'нет данных' || result === '') {
        result = 'Нет данных';
      }
      objectsResults.set(result, (objectsResults.get(result) || 0) + 1);
    });

    const resultKeys = Array.from(objectsResults.keys());
    const colors = resultKeys.map((result, idx) => {
      const resultColors = EXAM_RESULT_COLORS.resultColors as Record<string, string>;
      return resultColors[result] || EXAM_RESULT_COLORS.base[idx % EXAM_RESULT_COLORS.base.length];
    });

    return resultKeys.map((label, idx) => ({
      id: label,
      label: label === 'Не соответствует (фальсификат)' || label === 'Не соответствует НД (фальсификат)'
        ? 'Не соответствует\n(фальсификат)'
        : label,
      value: Array.from(objectsResults.values())[idx],
      color: colors[idx] || '#ccc',
    })).filter(item => item.value > 0);
  };

  const manufacturersPieData = getManufacturersPieData();
  const objectsPieData = getObjectsPieData();

  const handleDownload = () => startPdfExport(() => ({
    fileName: weldedJointsOnly ? 'welded_joint_exams_report.pdf' : 'lab_exam_report.pdf',
    summary: buildLabExamsSummary(
      filteredLabList,
      describeLabExamFilters(filteredExamResults, filteredYears, selectedRegions, filterFilters),
      weldedJointsOnly ? null : labExamDate,
      weldedJointsOnly,
    ),
    sections: [{
      kind: 'cards',
      title: registryName,
      cards: sortData(filteredLabList, detailInfoSortField, detailInfoSortDirection).map(buildLabExamRegistryCard),
    }],
  }));

  const uniqueManufacturers = useMemo(() => {
    const seen = new Set();
    return filteredLabList.filter((item: LabExam) => {
      if (!item.inn_company || seen.has(item.inn_company)) return false;
      seen.add(item.inn_company);
      return true;
    });
  }, [filteredLabList]);

  const currentManufacturers = useMemo(() => {
    const sortedData = sortData(uniqueManufacturers, manufacturersSortField, manufacturersSortDirection);
    const startIndex = (manufacturersPage - 1) * itemsPerPageManufacturers;
    const endIndex = startIndex + itemsPerPageManufacturers;
    return sortedData.slice(startIndex, endIndex);
  }, [uniqueManufacturers, manufacturersSortField, manufacturersSortDirection, manufacturersPage, itemsPerPageManufacturers]);

  const currentDetailInfoItems = useMemo(() => {
    const sortedData = sortData(filteredLabList, detailInfoSortField, detailInfoSortDirection);
    const startIndex = (detailInfoPage - 1) * itemsPerPageDetailInfo;
    const endIndex = startIndex + itemsPerPageDetailInfo;
    return sortedData.slice(startIndex, endIndex);
  }, [filteredLabList, detailInfoSortField, detailInfoSortDirection, detailInfoPage, itemsPerPageDetailInfo]);

  const getRegionStats = () => {
    if (selectedRegions.length === 0) {
      return {
        totalExams: filteredLabList.length,
        violationsCount: filteredLabList.filter(item =>
          item.exam_result && (
            item.exam_result.includes('Не соответствует') ||
            item.exam_result.includes('брак') ||
            item.exam_result.includes('фальсификат')
          )
        ).length,
        totalObjects: new Set(filteredLabList.map(item => item.object_adres)).size,
        totalOrganizations: new Set(filteredLabList.map(item => item.inn_company)).size
      };
    }

    const regionData = filteredLabList.filter(item =>
      item.region_company && selectedRegions.some(region =>
        region && item.region_company.toLowerCase() === region.toLowerCase()
      )
    );

    return {
      totalExams: regionData.length,
      violationsCount: regionData.filter(item =>
        item.exam_result && (
          item.exam_result.includes('Не соответствует') ||
          item.exam_result.includes('брак') ||
          item.exam_result.includes('фальсификат')
        )
      ).length,
      totalObjects: new Set(regionData.map(item => item.object_adres)).size,
      totalOrganizations: new Set(regionData.map(item => item.inn_company)).size
    };
  };

  const regionStats = getRegionStats();

  if (loading) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 9999
    }}>
      <img
        src="/loading.png"
        alt="Загрузка..."
        style={{ width: 200, height: 200, animation: 'spin 1.2s linear infinite' }}
      />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  if (error) return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      width: '100vw',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 9999,
      backgroundColor: 'rgba(255, 255, 255, 0.95)'
    }}>
      <div style={{
        textAlign: 'center',
        padding: '40px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        maxWidth: '500px'
      }}>
        <h3 style={{ color: '#e74c3c', marginBottom: '16px' }}>Ошибка загрузки</h3>
        <p style={{ marginBottom: '20px', color: '#666' }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '10px 20px',
            backgroundColor: '#0071bc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Попробовать снова
        </button>
      </div>
    </div>
  );

  return (
    <div ref={contentRef} className="App">
      <div className="navigation">
        <nav className="breadcrumbs">
          <span
            className="breadcrumb-home"
            style={{ color: 'rgba(0,0,0,0.5)', cursor: 'pointer', fontWeight: 400 }}
            onClick={() => navigate('/')}
          >
            Главная страница
          </span>
          <span style={{ margin: '0 8px', color: 'rgba(0,0,0,0.3)' }}>/</span>
          <span className="breadcrumb-current" style={{ fontWeight: 500, color: '#000' }}>
            {weldedJointsOnly ? 'Реестр испытаний сварных соединений' : 'Реестр лабораторных испытаний'}
          </span>
        </nav>
        <button className="download-button" onClick={handleDownload} disabled={pdfProgress !== null || filteredLabList.length === 0}>
          {getPdfButtonLabel(pdfProgress)}
        </button>
      </div>
      {pdfReport}
      {filteredLabList.length === 0 ? (
        <div style={{
          marginTop: 24,
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          padding: 24,
          textAlign: 'center',
          color: '#374151'
        }}>
          <h3 style={{ margin: 0, marginBottom: 8 }}>Нет результатов</h3>
          <div>По выбранным фильтрам не найдено данных. Измените параметры и попробуйте снова.</div>
        </div>
      ) : null}
      {isFilterOpen && (
        <div className="labexam-filter-modal-overlay">
          <div className="labexam-filter-modal">
            <button className="labexam-filter-modal__close" onClick={() => setIsFilterOpen(false)}>
              ×
            </button>
            <Filter
              data={labList}
              onFilterChange={handleFilterChange}
              examResults={filteredExamResults}
              onExamResultChange={handleExamResultChange}
              onRegionSelect={handleRegionSelect}
              selectedRegions={selectedRegions}
              onResetFilters={handleResetFilters}
              initialFilters={memoInitialFilters}
              open={isFilterOpen}
              showWeldingJointType={weldedJointsOnly}
            />
          </div>
        </div>
      )}
      {!filteredLabList.length ? null : <div className="main-content">
        {weldedJointsOnly ? null : (
        <div
          className="data-info-bar"
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            gap: 8,
            padding: '10px 16px',

          }}
        >
          <div style={{ fontSize: 14, color: '#333' }}>
            <span style={{ color: '#666' }}>Данные от</span>{' '}
            <b>{labExamDate || 'Нет данных'}</b>
          </div>
        </div>
        )}
        <div className="container">
          <div className="lab-exam-list">
            <div className="pdf-page" style={{ pageBreakAfter: 'always' }}>
              <div className="region-stats-container">
                <div className="region-stat-block">
                  <p>Количество проведенных испытаний в регионе</p>
                  <h1>{regionStats.totalExams}</h1>
                </div>

                <div className="region-stat-block">
                  <p>Количество объектов с нарушениями в регионе</p>
                  <h1>{regionStats.violationsCount}</h1>
                </div>

                <div className="region-stat-block">
                  <p>Общее количество проверенных объектов в регионе</p>
                  <h1>{regionStats.totalObjects}</h1>
                </div>

                <div className="region-stat-block">
                  <p>Количество организаций в регионе</p>
                  <h1>{regionStats.totalOrganizations}</h1>
                </div>
              </div>

              <div className="lab-exam-info">
                <div className="lab-exam-all">
                  <h2>Общее количество испытаний</h2>
                  <h1>{filteredLabList.length}</h1>
                </div>
                <div className="lab-exam-pie chart-container">
                  <h2 style={{ textAlign: "center", marginTop: "10px", marginBottom: "0px" }}>{registryName}. Соотношение результатов</h2>
                  <div
                    className="pie-chart"
                    style={{
                      height: 240,
                      width: 450,
                      minWidth: 200,
                      minHeight: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    <div style={{ width: 450, height: 240 }}>
                      <ResponsivePie
                        data={examResultsPieData}
                        margin={{ top: 20, right: viewportWidth <= 430 ? 200 : 270, bottom: 20, left: 20 }}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={3}
                        activeOuterRadiusOffset={16}
                        colors={examResultsPieData.map(d => d.color)}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        enableArcLabels={true}
                        arcLabel={d => String(d.value)}
                        enableArcLinkLabels={false}
                        theme={{
                          labels: {
                            text: {
                              fontWeight: 700,
                              fill: '#111',
                            },
                          },
                          legends: {
                            text: {
                              whiteSpace: 'pre-line',
                              fontWeight: 600,
                              fontSize: viewportWidth <= 430 ? 9 : 12,
                            },
                          },
                        }}
                        onClick={handlePieChartClickNivo}
                        activeId={filteredExamResults.length > 0 ? filteredExamResults[filteredExamResults.length - 1] : undefined}
                        legends={[{
                          anchor: 'right',
                          direction: 'column',
                          justify: false,
                          translateX: 110,
                          translateY: 0,
                          itemsSpacing: 4,
                          itemWidth: 90,
                          itemHeight: 18,
                          itemTextColor: '#999',
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 15,
                          symbolShape: 'circle',
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemTextColor: '#000',
                              },
                            },
                          ],
                        }]}
                        tooltip={({ datum }) => {
                          const total = examResultsPieData.reduce((sum, d) => sum + d.value, 0);
                          const percent = total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';
                          return (
                            <div style={{ padding: 8, background: '#fff', border: '1px solid #ccc', borderRadius: 4, whiteSpace: 'pre-line' }}>
                              <b>{datum.label}</b><br />
                              <b>{datum.value}</b> <span style={{ color: '#888', marginLeft: 8 }}>({percent}%)</span>
                            </div>
                          );
                        }}
                      />
                    </div>
                  </div>
                  {weldedJointsOnly && (
                    <p className="welded-joint-disclaimer">
                      Результаты испытаний не характеризуют исключительно качество продукции, так как сварка - сложный комплексный процесс, и её результаты зависят от многих факторов.
                    </p>
                  )}
                </div>
                <div className="lab-exam-manufacturers">
                  <h2>Общее количество производителей</h2>
                  <h1>{Array.from(new Set(filteredLabList.map((x: LabExam) => x.inn_company))).length}</h1>
                </div>
              </div>

              <div className="pdf-only-charts" style={{ margin: '32px 0', display: 'none', gap: '20px', flexWrap: 'wrap' }}>
                <div className="lab-exam-pie chart-container" style={{ flex: '1', minWidth: '400px' }}>
                  <h2 style={{ textAlign: "center", marginTop: "10px", marginBottom: "0px" }}>
                    {selectedRegions.length > 0
                      ? `${registryName}. Соотношение результатов у производителей в регионе: ${selectedRegions.join(', ')}`
                      : `${registryName}. Соотношение результатов у производителей`
                    }
                  </h2>
                  <div
                    className="pie-chart"
                    style={{
                      height: 240,
                      width: 450,
                      minWidth: 200,
                      minHeight: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    <div style={{ width: 450, height: 240 }}>
                      <ResponsivePie
                        data={manufacturersPieData}
                        margin={{ top: 20, right: 270, bottom: 20, left: 20 }}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={3}
                        activeOuterRadiusOffset={16}
                        colors={manufacturersPieData.map((d: any) => d.color)}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        enableArcLabels={true}
                        arcLabel={(d: any) => String(d.value)}
                        enableArcLinkLabels={false}
                        theme={{
                          labels: {
                            text: {
                              fontWeight: 700,
                              fill: '#111',
                            },
                          },
                          legends: {
                            text: {
                              whiteSpace: 'pre-line',
                              fontWeight: 600,
                              fontSize: 12,
                            },
                          },
                        }}
                        legends={[{
                          anchor: 'right',
                          direction: 'column',
                          justify: false,
                          translateX: 110,
                          translateY: 0,
                          itemsSpacing: 4,
                          itemWidth: 90,
                          itemHeight: 18,
                          itemTextColor: '#999',
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 15,
                          symbolShape: 'circle',
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemTextColor: '#000',
                              },
                            },
                          ],
                        }]}
                        tooltip={({ datum }) => {
                          const total = manufacturersPieData.reduce((sum: number, d: any) => sum + d.value, 0);
                          const percent = total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';
                          return (
                            <div style={{ padding: 8, background: '#fff', border: '1px solid #ccc', borderRadius: 4, whiteSpace: 'pre-line' }}>
                              <b>{datum.label}</b><br />
                              <b>{datum.value}</b> <span style={{ color: '#888', marginLeft: 8 }}>({percent}%)</span>
                            </div>
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="lab-exam-pie chart-container" style={{ flex: '1', minWidth: '400px' }}>
                  <h2 style={{ textAlign: "center", marginTop: "10px", marginBottom: "0px" }}>
                    {selectedRegions.length > 0
                      ? `${registryName}. Соотношение результатов на объектах в регионе: ${selectedRegions.join(', ')}`
                      : `${registryName}. Соотношение результатов на объектах`
                    }
                  </h2>
                  <div
                    className="pie-chart"
                    style={{
                      height: 240,
                      width: 450,
                      minWidth: 200,
                      minHeight: 200,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxSizing: 'border-box',
                    }}
                  >
                    <div style={{ width: 450, height: 240 }}>
                      <ResponsivePie
                        data={objectsPieData}
                        margin={{ top: 20, right: 270, bottom: 20, left: 20 }}
                        innerRadius={0.5}
                        padAngle={0.7}
                        cornerRadius={3}
                        activeOuterRadiusOffset={16}
                        colors={objectsPieData.map((d: any) => d.color)}
                        borderWidth={1}
                        borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                        enableArcLabels={true}
                        arcLabel={(d: any) => String(d.value)}
                        enableArcLinkLabels={false}
                        theme={{
                          labels: {
                            text: {
                              fontWeight: 700,
                              fill: '#111',
                            },
                          },
                          legends: {
                            text: {
                              whiteSpace: 'pre-line',
                              fontWeight: 600,
                              fontSize: 12,
                            },
                          },

                        }}
                        legends={[{
                          anchor: 'right',
                          direction: 'column',
                          justify: false,
                          translateX: 110,
                          translateY: 0,
                          itemsSpacing: 4,
                          itemWidth: 90,
                          itemHeight: 18,
                          itemTextColor: '#999',
                          itemDirection: 'left-to-right',
                          itemOpacity: 1,
                          symbolSize: 15,
                          symbolShape: 'circle',
                          effects: [
                            {
                              on: 'hover',
                              style: {
                                itemTextColor: '#000',
                              },
                            },
                          ],
                        }]}
                        tooltip={({ datum }) => {
                          const total = objectsPieData.reduce((sum: number, d: any) => sum + d.value, 0);
                          const percent = total > 0 ? ((datum.value / total) * 100).toFixed(1) : '0.0';
                          return (
                            <div style={{ padding: 8, background: '#fff', border: '1px solid #ccc', borderRadius: 4, whiteSpace: 'pre-line' }}>
                              <b>{datum.label}</b><br />
                              <b>{datum.value}</b> <span style={{ color: '#888', marginLeft: 8 }}>({percent}%)</span>
                            </div>
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="lab-bar-chart" style={{ marginTop: '32px', marginBottom: '0px', border: "2px solid #0071bc", borderRadius: "10px", display: viewportWidth >= 480 ? undefined : 'none' }}>
                  <p style={{ textAlign: "left", marginLeft: "20px", marginBottom: "0px", fontWeight: "600" }}>Динамика</p>
                  <div style={{ display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', padding: '0 20px' }}>
                    {Array.from(new Set(filteredLabList.map(x => x.exam_result))).sort().map((result, idx) => {
                      const colorMap: Record<string, string> = {
                        'Соответствует НД': 'rgba(126, 178, 70, 1)',
                        'Не соответствует НД (фальсификат)': 'rgba(237, 93, 98, 1)',
                        'Не соответствует НД (брак)': 'rgba(255, 166, 0, 1)',
                        'Нет данных': 'rgba(196, 196, 196, 1)'
                      };
                      const color = colorMap[result as string] || 'rgba(196, 196, 196, 1)';
                      return (
                        <div key={String(result)} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
                          <span style={{
                            display: 'inline-block',
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: color,
                            marginRight: 6,
                            border: '1.5px solid #bbb',
                          }} />
                          <span style={{ fontWeight: 500 }}>{String(result)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ width: '100%', height: 520, position: 'relative' }}>
                    <BarChartLabExam
                      data={filteredLabList}
                      filteredYears={filteredYears}
                      onBarClick={handleBarClick}
                    />
                  </div>
                </div>
            </div>

            <div className="pdf-page">
              <div className="detail-info">
                <p>{weldedJointsOnly ? 'Испытания сварных соединений. Подробная информация' : 'Лабораторные испытания. Подробная информация'}</p>
                <div className="items-per-page">
                  <label htmlFor="itemsPerPageDetailInfo">Количество на странице:</label>
                  <select id="itemsPerPageDetailInfo" value={itemsPerPageDetailInfo} onChange={(e) => setItemsPerPageDetailInfo(parseInt(e.target.value, 10))}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                    <option value={filteredLabList.length}>Все</option>
                  </select>
                </div>
                <DetailInfo
                  currentItems={currentDetailInfoItems}
                  sortField={detailInfoSortField}
                  sortDirection={detailInfoSortDirection}
                  onSort={handleDetailInfoSort}
                  onRowClick={handleOpenLabDetails}
                  weldedJointsOnly={weldedJointsOnly}
                />
                <Pagination
                  currentPage={detailInfoPage}
                  itemsPerPage={itemsPerPageDetailInfo}
                  paginate={(pageNumber) => setDetailInfoPage(pageNumber)}
                  labList={filteredLabList}
                />
              </div>
            </div>
          </div>
        </div>
      </div>}
    </div>
  );
}

export default LabExamPage;
